import type {
  FlightExplorerCopy,
  FlightExplorerPageCopy,
} from '@voll-intelligence/intelligence-ui-kit';
import { faresRequestCopy } from './fares_request_copy.js';
import { flightFaresCopy } from './flight_fares_copy.js';
import { fmtTripDates } from './flight_format.js';
import { flightPolicyCopy } from './flight_offer_copy.js';
import { genUiI18n } from './i18n.js';
import { legStepperCopy } from './leg_stepper_copy.js';
import { offlineRequestCopy } from './offline_request_copy.js';
import { flightFilterChipsCopy } from './flight_filter_chips_copy.js';
import { flightFilterCopy } from './flight_filter_copy.js';

/** Os textos da página do explorer no locale do host. */
function flightExplorerPageCopy(locale: string | null | undefined): FlightExplorerPageCopy {
  const { t, locale: lang } = genUiI18n(locale);
  return {
    listTitle: (city, date) => t('flightExplorer.listTitle', { city, date }),
    listTitleOutbound: (iata, city, date) =>
      t('flightExplorer.listTitleOutbound', { iata, city, date }),
    listTitleReturn: (iata, city, date) =>
      t('flightExplorer.listTitleReturn', { iata, city, date }),
    title: t('flightFilter.title'),
    tripDates: (depart, end) => fmtTripDates(depart, end, lang),
    found: (count) =>
      t(count === 1 ? 'flightFilter.found_one' : 'flightFilter.found_other', { count }),
    foundOfTotal: (count, total) => t('flightFilter.foundOfTotal', { count, total }),
    chooseOutbound: t('flightExplorer.chooseOutbound'),
    chooseReturn: t('flightExplorer.chooseReturn'),
    apply: t('flightFilter.apply'),
    loadMore: t('flightExplorer.loadMore'),
    share: t('flightExplorer.share'),
    copyLink: t('searchShare.copyLink'),
    downloadPdf: t('searchShare.downloadPdf'),
    pinnedFilter: (count) => t('flightExplorer.pinnedFilter', { count }),
    sortLabel: t('flightFilter.sort.label'),
    sortMenu: {
      rec: t('flightFilter.sortMenu.rec'),
      price: t('flightFilter.sortMenu.price'),
      dep: t('flightFilter.sortMenu.dep'),
      dur: t('flightFilter.sortMenu.dur'),
      arr: t('flightFilter.sortMenu.arr'),
    },
    chips: {
      policy: t('flightExplorer.chips.policy'),
      toolbar: t('flightExplorer.chips.toolbar'),
      back: t('flightExplorer.chips.back'),
    },
    chipLeg: {
      outbound: t('flightChips.leg.outbound'),
      inbound: t('flightChips.leg.inbound'),
      n: (n) => t('flightChips.leg.n', { n: String(n) }),
    },
    axisVia: t('flightChips.axis.via'),
    refreshing: t('flightExplorer.refreshing'),
    searching: t('flightExplorer.searching'),
    searchingLong: t('flightExplorer.searchingLong'),
    frozenEmpty: t('flightExplorer.frozenEmpty'),
    emptyResults: (destination) => t('flightExplorer.emptyResults', { destination }),
    emptyFilterTitle: t('flightFilter.empty.title'),
    emptyHint: t('flightFilter.empty.hint'),
    // Os rótulos do painel descem para minúsculas no locale e o `Intl.ListFormat`
    // (disjunção) põe o "ou" no lugar certo em cada idioma.
    emptyHintAxes: (axes) => {
      const names = axes.map((name) => name.toLocaleLowerCase(lang));
      const list = new Intl.ListFormat(lang, { style: 'long', type: 'disjunction' }).format(names);
      return t('flightFilter.empty.hintAxes', { axes: list });
    },
    why: {
      recommended: t('flightExplorer.whyRecommended'),
      direct: t('flightExplorer.whyRecommendedDirect'),
      inPolicy: t('flightExplorer.whyRecommendedInPolicy'),
      directInPolicy: t('flightExplorer.whyRecommendedDirectInPolicy'),
    },
    newSearch: t('searchEmpty.newSearch'),
    retry: t('searchEmpty.retry'),
    tryAgain: t('common.tryAgain'),
    error: t('flightExplorer.error'),
    timeout: t('flightExplorer.timeout'),
    timeoutHint: t('flightExplorer.timeoutHint'),
    timeoutCity: (city) => t('flightExplorer.timeoutCity', { city }),
    timeoutCityHint: t('flightExplorer.timeoutCityHint'),
    timeoutAirports: t('flightExplorer.timeoutAirports'),
    policyEvaluation: t('flightExplorer.policyEvaluation'),
    policyEvaluationHint: t('flightExplorer.policyEvaluationHint'),
    searchExpired: t('flightExplorer.searchExpired'),
    searchExpiredHint: t('flightExplorer.searchExpiredHint'),
    searchExpiredAction: t('flightExplorer.searchExpiredAction'),
    outboundGone: t('flightExplorer.outboundGone'),
    airportSwitchNotice: (arrival) => t('flightExplorer.airportSwitchNotice', { arrival }),
    researchKept: t('flightExplorer.researchKept'),
    researchKeptCabin: (cabin) => t('flightExplorer.researchKeptCabin', { cabin }),
    researchEmptyCabin: (cabin) => t('flightExplorer.researchEmptyCabin', { cabin }),
    researchKeptPolicy: t('flightExplorer.researchKeptPolicy'),
    replacesTrip: t('flightExplorer.replacesTrip'),
    replacesTripDiscards: t('flightExplorer.replacesTripDiscards'),
    draftPending: t('flightExplorer.draftPending'),
    confirmUnavailable: t('flightExplorer.confirmUnavailable'),
    offline: offlineRequestCopy(t, 'flight'),
    offlineReturnOn: (date) => t('flightExplorer.offline.returnOn', { date }),
  };
}

/** Todos os textos do explorer do kit no locale do host (#4997, D2): função
 *  pura, que a bancada do web-chat e a casca do chat chamam com o próprio
 *  locale. O host memoiza por locale. */
export function flightExplorerCopy(locale: string | null | undefined): FlightExplorerCopy {
  return {
    page: flightExplorerPageCopy(locale),
    fares: flightFaresCopy(locale),
    request: faresRequestCopy(locale),
    policy: flightPolicyCopy(locale),
    filter: flightFilterCopy(locale),
    chips: flightFilterChipsCopy(locale),
    stepper: legStepperCopy(locale),
  };
}
